import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft } from "lucide-react";
import type { MenuSection, ServiceMenuLink } from "./types";

interface HeaderDesktopNavProps {
  menuSections: MenuSection[];
  isMenuLoading: boolean;
  serviceMenuLinks: ServiceMenuLink[];
  isLoggedIn: boolean;
  activeCategoryHandle?: string | null;
  tm: (text: string) => string;
  th: (key: string, fallback: string) => string;
}

export const HeaderDesktopNav = ({
  menuSections,
  isMenuLoading,
  serviceMenuLinks,
  isLoggedIn,
  activeCategoryHandle,
  tm,
  th,
}: HeaderDesktopNavProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [isServiceMenuHovered, setIsServiceMenuHovered] = useState(false);

  return (
    <nav className="hidden lg:block border-t border-border/50 relative">
      <div className="flex items-center justify-center gap-8 h-12 px-4 lg:px-8 text-base">
        <Link
          to="/"
          className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname === "/" ? "font-bold" : "hover:after:scale-x-100 hover:after:origin-bottom-right"}`}
          translate="no"
        >
          {tm("ראשי")}
        </Link>
        {isMenuLoading && menuSections.length === 0 && (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 w-16 bg-primary/10 rounded animate-pulse" />
            ))}
          </>
        )}
        {menuSections.map((section) => {
          const currentCategoryHandle =
            activeCategoryHandle !== undefined
              ? activeCategoryHandle
              : location.pathname.startsWith("/collections/")
              ? decodeURIComponent(location.pathname.replace("/collections/", ""))
              : null;

          const isSectionActive =
            currentCategoryHandle === null
              ? false
              : section.directCollectionHandle
              ? currentCategoryHandle === section.directCollectionHandle
              : section.titleCollectionHandles?.length &&
                section.titleCollectionHandles.includes(currentCategoryHandle!)
              ? true
              : section.subsections.some((sub) =>
                  sub.collections.some((col) => col.node.handle === currentCategoryHandle)
                );

          return (
            <div
              key={section.title}
              className="relative group"
              onMouseEnter={() => !section.directCollectionHandle && setHoveredSection(section.title)}
              onMouseLeave={() => setHoveredSection(null)}
            >
              {section.directCollectionHandle ? (
                <Link
                  to={`/collections/${section.directCollectionHandle}`}
                  className={`text-base transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 h-12 flex items-center ${
                    isSectionActive
                      ? "text-primary font-semibold after:scale-x-100"
                      : "text-primary hover:text-primary/80 after:scale-x-0 hover:after:scale-x-100 hover:after:origin-bottom-right"
                  }`}
                >
                  {tm(section.title)}
                </Link>
              ) : section.titleCollectionHandles?.length ? (
                <Link
                  to={`/collections/all?filter=${section.titleCollectionHandles.join(",")}`}
                  className={`text-base transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 h-12 flex items-center gap-1 ${
                    isSectionActive
                      ? "text-primary font-semibold after:scale-x-100"
                      : "text-primary hover:text-primary/80 after:scale-x-0 hover:after:scale-x-100 hover:after:origin-bottom-right"
                  }`}
                >
                  {tm(section.title)}
                  {section.subsections.length > 0 && (
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform duration-200 ${
                        hoveredSection === section.title ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>
              ) : (
                <button
                  className={`text-base transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 h-12 flex items-center gap-1 ${
                    isSectionActive
                      ? "text-primary font-semibold after:scale-x-100"
                      : "text-primary hover:text-primary/80 after:scale-x-0 hover:after:scale-x-100 hover:after:origin-bottom-right"
                  }`}
                >
                  {tm(section.title)}
                  {section.subsections.length > 0 && (
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform duration-200 ${
                        hoveredSection === section.title ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>
              )}
            </div>
          );
        })}
        <Link
          to="/blog"
          className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname.startsWith("/blog") ? "font-bold" : "hover:after:scale-x-100 hover:after:origin-bottom-right"}`}
          translate="no"
        >
          {tm("בלוג")}
        </Link>
        <Link
          to="/about"
          className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname === "/about" ? "font-bold" : "hover:after:scale-x-100 hover:after:origin-bottom-right"}`}
          translate="no"
        >
          {tm("אודות")}
        </Link>
        <Link
          to="/gift-card"
          className={`notranslate text-base text-primary hover:text-primary/80 transition-colors duration-300 relative h-12 flex items-center after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 ${location.pathname === "/gift-card" ? "font-bold" : "hover:after:scale-x-100 hover:after:origin-bottom-right"}`}
          translate="no"
        >
          GiftCard
        </Link>
        <div
          className="relative notranslate"
          translate="no"
          onMouseEnter={() => setIsServiceMenuHovered(true)}
          onMouseLeave={() => setIsServiceMenuHovered(false)}
        >
          <button
            className={`text-base text-primary hover:text-primary/80 transition-colors duration-300 relative after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-left after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-right h-12 flex items-center gap-1 ${
              ["/contact", "/faq", "/ring-size-guide", "/accessibility"].includes(location.pathname)
                ? "font-bold"
                : ""
            }`}
          >
            {th("customerService", "שירות לקוחות")}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform duration-200 ${
                isServiceMenuHovered ? "rotate-180" : ""
              }`}
            />
          </button>

          <div
            className={`absolute top-full right-0 min-w-[200px] bg-background border border-border/30 shadow-lg z-50 overflow-hidden transition-all duration-200 ${
              isServiceMenuHovered
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 -translate-y-2 pointer-events-none"
            }`}
          >
            <div className="py-3 px-1">
              {serviceMenuLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.requiresAuth && !isLoggedIn ? "/auth" : link.href}
                  onClick={() => {
                    if (link.requiresAuth && !isLoggedIn) {
                      navigate("/auth", { state: { redirectTo: link.href } });
                    }
                    setIsServiceMenuHovered(false);
                  }}
                  className={`block px-5 py-2.5 text-sm transition-all duration-150 ${
                    location.pathname === link.href.split("?")[0]
                      ? "text-primary font-medium bg-champagne/50"
                      : "text-foreground/70 hover:text-primary hover:bg-champagne/30"
                  }`}
                >
                  {link.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Mega Menu */}
      {menuSections.map(
        (section) =>
          !section.directCollectionHandle &&
          section.subsections.length > 0 && (
            <div
              key={`mega-${section.title}`}
              className={`absolute top-full left-1/2 -translate-x-1/2 w-[950px] bg-background border border-border/40 shadow-xl z-50 overflow-hidden transition-all duration-300 ${
                hoveredSection === section.title
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 -translate-y-3 pointer-events-none"
              }`}
              onMouseEnter={() => setHoveredSection(section.title)}
              onMouseLeave={() => setHoveredSection(null)}
            >
              <div className="flex h-[340px]">
                {section.image && (
                  <Link
                    to={
                      section.titleCollectionHandles?.length
                        ? `/collections/all?filter=${section.titleCollectionHandles.join(",")}`
                        : `/collections/${section.subsections[0]?.collections[0]?.node.handle || ""}`
                    }
                    onClick={() => setHoveredSection(null)}
                    className="w-[300px] relative overflow-hidden flex-shrink-0 group cursor-pointer"
                  >
                    {hoveredSection === section.title && (
                      <img
                        src={section.image.url}
                        alt={section.image.altText || section.title}
                        width={300}
                        height={340}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 right-0 left-0 p-6">
                      <span className="text-white text-xl font-bold">{tm(section.title)}</span>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-white/80 text-sm">{tm("לצפייה בקולקציה")}</span>
                        <ChevronLeft className="w-4 h-4 text-white/80 group-hover:-translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                )}

                <div className="flex-1 bg-background">
                  <div className="h-full flex">
                    {section.subsections.map((subsection, idx) => (
                      <div
                        key={idx}
                        className={`flex-1 py-6 px-5 ${
                          idx !== section.subsections.length - 1 ? "border-l border-border/30" : ""
                        }`}
                      >
                        {subsection.title && (
                          <div className="mb-4 pb-3 border-b border-primary/20">
                            <h3 className="text-sm font-bold text-primary uppercase tracking-wider">
                              {tm(subsection.title)}
                            </h3>
                          </div>
                        )}
                        <div className="space-y-0.5">
                          {subsection.collections.map((collection) => {
                            const isCollectionActive =
                              decodeURIComponent(location.pathname) ===
                              `/collections/${collection.node.handle}`;
                            return (
                              <Link
                                key={collection.node.id}
                                to={`/collections/${collection.node.handle}`}
                                onClick={() => setHoveredSection(null)}
                                className={`flex items-center gap-2 py-2 px-3 text-sm transition-all duration-200 group/item ${
                                  isCollectionActive
                                    ? "text-primary font-semibold bg-primary/10"
                                    : "text-foreground/70 hover:text-primary hover:bg-champagne/50"
                                }`}
                              >
                                {isCollectionActive && (
                                  <span className="w-1 h-4 bg-primary flex-shrink-0" />
                                )}
                                <span
                                  className={`${
                                    !isCollectionActive
                                      ? "group-hover/item:translate-x-[-4px] transition-transform duration-200"
                                      : ""
                                  }`}
                                >
                                  {subsection.collectionTitles?.[collection.node.handle] ||
                                    tm(collection.node.title)}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
      )}
    </nav>
  );
};
